<script setup lang="ts">
import { computed, ref, toRefs, type PropType, useAttrs, type StyleValue, watch } from 'vue'
import { type FileDTO } from '@/composables/media/media'
import { vNoSave as vNoSaveOrigin } from '@/directives/noSave'

defineOptions({
  inheritAttrs: false
})

const attrs = useAttrs()

const props = defineProps({
  /** Mode A: Image URL string */
  src: {
    type: String,
    default: ''
  },
  /** Mode B: FileDTO object (prioritized over src) */
  file: {
    type: Object as PropType<FileDTO | null>,
    default: null
  },
  /** Aspect Ratio (width / height) - if provided, maintains this ratio */
  aspectRatio: {
    type: Number,
    default: 0
  },
  /** Enable copy protection (disable right click, drag, save) */
  noSave: {
    type: Boolean,
    default: false
  },
  /** Image quality to use when file object is provided */
  quality: {
    type: String as PropType<'url' | 'lowQuality' | 'middleQuality' | 'highQuality'>,
    default: 'url'
  },
  /** CSS object-fit property */
  objectFit: {
    type: String as PropType<'contain' | 'cover' | 'fill' | 'none' | 'scale-down'>,
    default: 'cover'
  },
  /** Background color for the skeleton loader */
  skeletonColor: {
    type: String,
    default: '#D1D1D1'
  },
  /** Show transparency grid background when loaded */
  transparentBg: {
    type: Boolean,
    default: false
  }
})

const { src, file, aspectRatio, noSave, objectFit, skeletonColor, quality, transparentBg } = toRefs(props)

// State
const isLoading = ref(true)
const hasError = ref(false)

const finalSrc = computed(() => {
  if (file.value) {
    const selectedUrl = (file.value as any)[quality.value]
    if (selectedUrl) return selectedUrl
    return file.value.url
  }
  return src.value
})

watch(finalSrc, () => {
  isLoading.value = true
  hasError.value = false
})

const computedAspectRatio = computed(() => {
  // 优先级 1: 如果 Props 显式传了 aspectRatio，直接用
  if (aspectRatio.value > 0) return aspectRatio.value

  // 优先级 2: 如果 file 对象中有宽高信息，自动计算
  // 假设你的 FileDTO 里有 width 和 height 字段
  if (file.value && (file.value as any).w && (file.value as any).h) {
    const w = Number((file.value as any).w)
    const h = Number((file.value as any).h)
    if (h > 0) return w / h
  }

  // 优先级 3: 没有任何尺寸信息，且不是 URL 模式，为了避免塌陷，给一个默认比例 (可选)
  // return 16 / 9

  return undefined
})

// Loading Handlers
const onLoad = () => {
  isLoading.value = false
}

const onError = () => {
  isLoading.value = false
  hasError.value = true
}

// Wrapper Directive to respect noSave prop
const vNoSave = {
  mounted(el: HTMLElement, binding: any) {
    if (binding.value) {
      vNoSaveOrigin.mounted(el)
    }
  },
  updated(el: HTMLElement, binding: any) {
    // If value changed from false to true, we might need to mount?
    // The original directive doesn't have an 'updated' hook handling dynamic toggle well
    // because it appends DOM elements.
    // For simplicity, we assume noSave doesn't toggle frequently or we stick to mounted/unmounted.
    // However, if binding.value becomes true, we should probably run mounted.
    // If it becomes false, we should run unmounted.
    if (binding.value !== binding.oldValue) {
      if (binding.value) {
        vNoSaveOrigin.mounted(el)
      } else {
        vNoSaveOrigin.unmounted(el)
      }
    }
  },
  unmounted(el: HTMLElement, binding: any) {
    if (binding.value) {
      vNoSaveOrigin.unmounted(el)
    }
  }
}

// Attribute Separation
const wrapperClass = computed(() => attrs.class)
const wrapperStyle = computed(() => attrs.style as StyleValue)
const imgAttrs = computed(() => {
  const { class: _c, style: _s, ...rest } = attrs
  return rest
})

// Styles
const containerStyle = computed(() => {
  const styles: any = {
    // Ensure container has relative positioning for the absolute skeleton
    position: 'relative',
    overflow: 'hidden',
    display: 'block'
    //width: '100%'
  }

  // Set background color via inline style to support skeletonColor prop
  // Only set it if we are NOT showing the transparency grid (i.e. either not requested, or still loading)
  if (!transparentBg.value || isLoading.value) {
    styles.backgroundColor = skeletonColor.value
  }

  // 应用计算出的宽高比
  if (computedAspectRatio.value) {
    styles.aspectRatio = computedAspectRatio.value
  }

  return styles
})

const imgStyle = computed(() => {
  const isRatioSet = computedAspectRatio.value !== undefined
  return {
    width: '100%',
    height: '100%',
    objectFit: objectFit.value,
    opacity: isLoading.value ? 0 : 1,

    // 如果容器已经被 aspect-ratio 撑开了，图片就 absolute 覆盖上去
    // 如果没有宽高比（纯 URL 模式），则维持 block 让图片自己撑开（此时无法避免塌陷，除非外部 CSS 设定了高度）
    position: (isRatioSet ? 'absolute' : 'static') as 'absolute' | 'static',
    top: isRatioSet ? 0 : undefined,
    left: isRatioSet ? 0 : undefined
  }
})
</script>

<template>
  <div
    class="smart-image-container"
    :style="[containerStyle, wrapperStyle]"
    :class="[wrapperClass, { 'transparency-grid': transparentBg && !isLoading }]">
    <!-- Loading Spinner -->
    <div v-if="isLoading" class="loading-spinner-container">
      <svg class="spinner-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <g fill="none" stroke="#FFFFFF" stroke-linecap="round" stroke-width="2">
          <path
            stroke-dasharray="60"
            stroke-dashoffset="60"
            stroke-opacity="0.3"
            d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3Z">
            <animate fill="freeze" attributeName="stroke-dashoffset" dur="1.95s" values="60;0" />
          </path>
          <path stroke-dasharray="15" stroke-dashoffset="15" d="M12 3C16.9706 3 21 7.02944 21 12">
            <animate fill="freeze" attributeName="stroke-dashoffset" dur="0.45s" values="15;0" />
            <animateTransform
              attributeName="transform"
              dur="2.25s"
              repeatCount="indefinite"
              type="rotate"
              values="0 12 12;360 12 12" />
          </path>
        </g>
      </svg>
    </div>

    <!-- Image -->
    <img
      v-if="finalSrc"
      :src="finalSrc"
      :style="imgStyle"
      v-bind="imgAttrs"
      v-no-save="noSave"
      @load="onLoad"
      @error="onError" />

    <!-- Optional Error State Visual -->
    <div v-if="hasError && !isLoading" class="error-placeholder">
      <span class="text-xs text-gray-400">Failed</span>
    </div>
  </div>
</template>

<style scoped>
.smart-image-container {
  /* background-color removed here, handled in containerStyle */
  border-radius: inherit;
  /* Inherit border-radius from parent if set via class */
}

.transparency-grid {
  background-color: #fff;
  background-image:
    linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%);
  background-size: 20px 20px;
  background-position:
    0 0,
    0 10px,
    10px -10px,
    -10px 0px;
}

/* Loading Spinner Container */
.loading-spinner-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
}

.spinner-icon {
  width: 20%;
  min-width: 32px;
  max-width: 80px;
  height: auto;
  aspect-ratio: 1;
}

.error-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  z-index: 5;
}
</style>
